import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../lib/api';
import AssistantWidget from './AssistantWidget';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import GlobalSearch from './GlobalSearch';
import { rollenName } from '../lib/rollen';
import { erinnerungZeigen, LOGIN_MERKZEICHEN, EINRICHTEN_LINK } from '../lib/zweiFaktor';
import { Erinnerung } from './zweifaktor/Erinnerung';
import { AnleitungDialog } from './zweifaktor/AnleitungDialog';
import {
  Clock,
  Users,
  Settings,
  LogOut,
  Menu,
  FileText,
  User,
  ChevronDown,
  LayoutDashboard,
  UserCog,
  Link as LinkIcon,
  Landmark,
  ShieldAlert,
} from 'lucide-react';

// Seiten mit breiten Tabellen bekommen mehr Platz als der Standard-Container.
// Neue Listenseiten hier eintragen, sonst laufen sie im 1280px-Container.
const WIDE_ROUTES = ['/admin/assignments', '/admin/employees', '/admin/jugendaemter'];

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isAdminLike, mustChangePassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const containerMaxWidth = WIDE_ROUTES.includes(location.pathname) ? 'max-w-[1500px]' : 'max-w-7xl';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoData, setLogoData] = useState(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await settingsAPI.getBranding();
        if (res.data?.logo_data) {
          setLogoData(res.data.logo_data);
        }
      } catch (e) { /* ignore */ }
    };
    fetchBranding();
  }, []);

  // 2FA-Stand nur aus user im Context (kein eigener Zwischenspeicher); undefined = noch nicht geladen.
  const verwaltung = isAdminLike();
  const zweiFaktorAktiv = user?.zwei_faktor_aktiv;
  const faellig = user?.zwei_faktor_erinnerung_faellig;
  const [erinnerungOffen, setErinnerungOffen] = useState(false);
  const [anleitungOffen, setAnleitungOffen] = useState(false);
  const erinnerungGeprueft = useRef(false);

  useEffect(() => {
    if (erinnerungGeprueft.current || !verwaltung || zweiFaktorAktiv === undefined || mustChangePassword) return;
    erinnerungGeprueft.current = true;
    let merkzeichen = false;
    try {
      merkzeichen = sessionStorage.getItem(LOGIN_MERKZEICHEN) === '1';
      sessionStorage.removeItem(LOGIN_MERKZEICHEN);
    } catch { /* privater Modus */ }
    if (erinnerungZeigen({ verwaltung, aktiv: zweiFaktorAktiv, faellig, merkzeichen, mustChangePassword })) {
      setErinnerungOffen(true);
    }
  }, [verwaltung, zweiFaktorAktiv, faellig, mustChangePassword]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const employeeNavItems = [
    { path: '/', label: 'Stundenzettel', icon: FileText },
  ];

  const adminNavItems = [
    { path: '/', label: 'Übersicht', icon: LayoutDashboard },
    { path: '/admin/users', label: 'Mitarbeiter', icon: Users },
    { path: '/admin/assignments', label: 'Zuordnungen', icon: LinkIcon },
    { path: '/admin/jugendaemter', label: 'Jugendämter', icon: Landmark },
  ];

  const navItems = isAdminLike() ? adminNavItems : employeeNavItems;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const LogoIcon = () => {
    if (logoData) {
      return <img src={logoData} alt="Logo" className="h-9 w-9 object-contain rounded-lg" data-testid="header-custom-logo" />;
    }
    return (
      <div className="h-9 w-9 bg-blue-800 rounded-lg flex items-center justify-center">
        <Clock className="h-5 w-5 text-white" />
      </div>
    );
  };

  const NavLinks = ({ mobile = false }) => (
    <nav className={`flex ${mobile ? 'flex-col space-y-1' : 'items-center space-x-1'}`}>
      {navItems.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => mobile && setMobileOpen(false)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              isActive(item.path)
                ? 'bg-blue-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            } ${mobile ? 'w-full' : ''}`}
            data-testid={`nav-${item.path.replace(/\//g, '-')}`}
          >
            {mobile && <Icon className="h-5 w-5" />}
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        {/* Wie im PersoNYzer: Hinweis als erstes Element im Header, über der Navigation. */}
        {verwaltung && zweiFaktorAktiv === false && (
          <Link to={EINRICHTEN_LINK} className="block bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors" data-testid="zwei-faktor-streifen">
            <div className={`${containerMaxWidth} mx-auto flex items-center gap-2 px-4 py-2 text-sm sm:px-6 lg:px-8`}>
              <ShieldAlert className="h-4 w-4 shrink-0" />
              <span>Ihr Konto ist noch ohne Zwei-Faktor-Anmeldung. <strong className="underline">Jetzt einrichten</strong></span>
            </div>
          </Link>
        )}
        <div className={`${containerMaxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="flex items-center justify-between h-16" data-testid="kopf-navigation">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                <LogoIcon />
                <span className="text-xl font-bold text-slate-900 font-[Manrope]">
                  TimeNYzer
                </span>
              </Link>
              <div className="max-md:hidden">
                <NavLinks />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isAdminLike() && <GlobalSearch />}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-3"
                    data-testid="user-menu-trigger"
                  >
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-blue-800" />
                    </div>
                    <span className="max-sm:hidden text-sm font-medium text-slate-700">
                      {user?.first_name} {user?.last_name}
                    </span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-slate-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                      isAdminLike() ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {rollenName(user?.role)}
                    </span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    data-testid="profile-menu-item"
                  >
                    <UserCog className="mr-2 h-4 w-4" />
                    Profil
                  </DropdownMenuItem>
                  {isAdminLike() && (
                    <DropdownMenuItem
                      onClick={() => navigate('/admin/settings')}
                      data-testid="settings-menu-item"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      Einstellungen
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600"
                    data-testid="logout-menu-item"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72">
                  <div className="flex items-center gap-2 mb-8 mt-4">
                    <LogoIcon />
                    <span className="text-xl font-bold text-slate-900 font-[Manrope]">
                      TimeNYzer
                    </span>
                  </div>
                  <NavLinks mobile />
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <main className={`${containerMaxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8`}>
        {children}
      </main>

      <AssistantWidget />
      {verwaltung && (
        <>
          <Erinnerung open={erinnerungOffen} onClose={() => setErinnerungOffen(false)} onAnleitung={() => setAnleitungOffen(true)} />
          <AnleitungDialog open={anleitungOffen} onOpenChange={setAnleitungOffen} />
        </>
      )}
    </div>
  );
};

export default Layout;
